'use client';

import { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import PageAtmosphere from '@/components/PageAtmosphere';
import { useT } from '@/lib/useT';
import {
  Truck, MapPin, Star, Package, Search,
  ChevronLeft, ChevronRight, Building2,
} from 'lucide-react';

// v2 palette — keep in sync with landing-v2.css (--bg, --accent, etc.)
// Hardcoded inline instead of pulling the .lv2 wrapper because that wrapper's
// universal selector zeroes every margin/padding inside, fighting Tailwind.
const V2 = {
  bg:        'var(--c-bg)',
  bg2:       'var(--c-panel)',
  bg3:       'var(--c-card)',
  text:      'var(--c-text)',
  heading:   'var(--c-heading)',
  textMute:  'var(--c-muted)',
  line:      'var(--c-border)',
  accent:    'var(--accent)',
  accentSoft:'var(--accent-soft)',
  accentBorder:'color-mix(in srgb, var(--accent) 20%, transparent)',
};

/* ─── types — mirror carrier-profile.service.listPublic() ─────────── */
interface CarrierItem {
  id: string;
  orgId: string;
  headline: string | null;
  description: string | null;
  cargoTypes: string[];
  serviceCountries: string[];
  rating: number | null;
  ratingCount: number;
  deliveriesCompleted: number;
  publishedAt: string | null;
  org: {
    id: string;
    name: string;
    slug: string;
    logo: string | null;
    brandColor: string | null;
    country: string | null;
    city: string | null;
  } | null;
}

interface CarriersResponse {
  page: number;
  total: number;
  limit: number;
  data: CarrierItem[];
}

/* ─── helpers ─────────────────────────────────────────────────────── */
const FLAGS: Record<string, string> = {
  DE: '\u{1F1E9}\u{1F1EA}', AT: '\u{1F1E6}\u{1F1F9}', CH: '\u{1F1E8}\u{1F1ED}',
  PL: '\u{1F1F5}\u{1F1F1}', CZ: '\u{1F1E8}\u{1F1FF}', UA: '\u{1F1FA}\u{1F1E6}',
  RU: '\u{1F1F7}\u{1F1FA}', MD: '\u{1F1F2}\u{1F1E9}', BG: '\u{1F1E7}\u{1F1EC}',
  KZ: '\u{1F1F0}\u{1F1FF}', NL: '\u{1F1F3}\u{1F1F1}', BE: '\u{1F1E7}\u{1F1EA}',
  FR: '\u{1F1EB}\u{1F1F7}', IT: '\u{1F1EE}\u{1F1F9}', ES: '\u{1F1EA}\u{1F1F8}',
  EE: '\u{1F1EA}\u{1F1EA}', LV: '\u{1F1F1}\u{1F1FB}', LT: '\u{1F1F1}\u{1F1F9}',
  RO: '\u{1F1F7}\u{1F1F4}', HU: '\u{1F1ED}\u{1F1FA}',
};

const CARGO_TYPE_KEYS = ['general', 'refrigerated', 'hazmat', 'oversize', 'pallet', 'bulk', 'liquid'];

// Strip trailing /api so we can prefix /api/marketplace ourselves.
const RAW_API = process.env.NEXT_PUBLIC_API_URL || '';
const API = RAW_API.replace(/\/api$/, '');

/* ─── component ───────────────────────────────────────────────────── */
export default function MarketplaceIndexPage() {
  const t = useT();
  const [data, setData] = useState<CarriersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('');
  const [cargoType, setCargoType] = useState('');
  const [page, setPage] = useState(1);

  const fetchCarriers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (country) params.set('country', country);
      if (cargoType) params.set('cargoType', cargoType);
      params.set('page', String(page));

      const res = await fetch(`${API}/api/marketplace/carriers?${params.toString()}`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch {
      // silent — show empty state below
    } finally {
      setLoading(false);
    }
  }, [country, cargoType, page]);

  useEffect(() => { fetchCarriers(); }, [fetchCarriers]);

  const carriers = data?.data ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <>
      <PublicHeader />
      <PageAtmosphere />
      <div className="min-h-screen" style={{ background: V2.bg, color: V2.text }}>

      {/* ════════════ PAGE TITLE ════════════ */}
      <section className="relative overflow-hidden px-6 md:px-8 pt-20 pb-12 max-w-7xl mx-auto text-center">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, color-mix(in srgb, var(--accent) 8%, transparent) 0%, transparent 70%)' }} />

        <div className="relative z-10 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-xs font-semibold tracking-wide uppercase"
            style={{ background: V2.accentSoft, color: V2.accent, border: `1px solid ${V2.accentBorder}` }}>
            <Package size={14} /> {t('mkt_index_badge')}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4" style={{ color: V2.heading }}>
            {t('mkt_index_title')} <span style={{ color: V2.accent }}>{t('mkt_index_title_accent')}</span>
          </h1>
          <p className="text-base md:text-lg max-w-xl mx-auto" style={{ color: V2.textMute }}>
            {t('mkt_index_sub')}
          </p>
        </div>
      </section>

      {/* ════════════ FILTERS ════════════ */}
      <section className="px-6 md:px-8 pb-8 max-w-7xl mx-auto">
        <div className="p-4 md:p-6 flex flex-col md:flex-row gap-4 rounded-2xl"
          style={{ background: V2.bg2, border: `1px solid ${V2.line}` }}>
          {/* Country */}
          <select
            value={country}
            onChange={(e) => { setCountry(e.target.value); setPage(1); }}
            className="px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 appearance-none cursor-pointer"
            style={{ background: V2.bg3, border: `1px solid ${V2.line}`, color: V2.text, minWidth: 200 }}
          >
            <option value="">{t('mkt_index_all_countries')}</option>
            {Object.keys(FLAGS).map((c) => (
              <option key={c} value={c}>{FLAGS[c]} {c}</option>
            ))}
          </select>

          {/* Cargo type */}
          <select
            value={cargoType}
            onChange={(e) => { setCargoType(e.target.value); setPage(1); }}
            className="px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 appearance-none cursor-pointer"
            style={{ background: V2.bg3, border: `1px solid ${V2.line}`, color: V2.text, minWidth: 220 }}
          >
            <option value="">{t('mkt_index_all_cargo')}</option>
            {CARGO_TYPE_KEYS.map((k) => (
              <option key={k} value={k}>{t(`mkt_cargo_${k}`)}</option>
            ))}
          </select>

          {/* Spacer + count */}
          <div className="flex-1 flex items-center justify-end text-sm" style={{ color: V2.textMute }}>
            {data && (
              <span>{data.total} {t('mkt_index_results')}</span>
            )}
          </div>
        </div>
      </section>

      {/* ════════════ CARRIER GRID ════════════ */}
      <section className="px-6 md:px-8 pb-12 max-w-7xl mx-auto">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: V2.accent, borderTopColor: 'transparent' }} />
            <p className="mt-4 text-sm" style={{ color: V2.textMute }}>{t('mkt_index_loading')}</p>
          </div>
        ) : carriers.length === 0 ? (
          <div className="text-center py-20">
            <Truck size={48} className="mx-auto mb-4" style={{ color: V2.textMute }} />
            <p className="text-lg font-bold mb-2" style={{ color: V2.heading }}>{t('mkt_index_empty')}</p>
            <p className="text-sm" style={{ color: V2.textMute }}>{t('mkt_index_empty_hint')}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <style>{`.lv3pc{transition:transform .2s ease,border-color .2s ease,box-shadow .2s ease}.lv3pc:hover{transform:translateY(-5px);box-shadow:0 18px 50px color-mix(in srgb, var(--accent) 12%, transparent);border-color:color-mix(in srgb, var(--accent) 40%, transparent)!important}`}</style>
              {carriers.map((c) => {
                if (!c.org) return null;
                const accent = c.org.brandColor || 'var(--accent)';
                return (
                  <Link href={`/marketplace/${c.org.slug}`} key={c.id}
                    className="lv3pc group block overflow-hidden"
                    style={{ background: 'var(--c-card)', border: `1px solid var(--c-border)`, borderRadius: 16 }}>
                    {/* Brand accent top bar */}
                    <div className="h-1" style={{ background: accent }} />

                    <div className="p-6">
                      {/* Logo + name */}
                      <div className="flex items-start gap-3 mb-4">
                        {c.org.logo ? (
                          <img src={c.org.logo} alt={c.org.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ background: `${accent}20`, color: accent }}>
                            <Building2 size={20} />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <h3 className="text-base font-bold leading-tight group-hover:text-[var(--accent)] transition-colors line-clamp-1"
                            style={{ color: V2.heading }}>
                            {c.org.name}
                          </h3>
                          {c.org.city && (
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className="text-sm">{FLAGS[c.org.country || ''] ?? ''}</span>
                              <span className="text-xs" style={{ color: V2.textMute }}>{c.org.city}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Headline */}
                      {c.headline && (
                        <p className="text-sm font-medium mb-2 line-clamp-2" style={{ color: V2.text }}>{c.headline}</p>
                      )}

                      {/* Rating */}
                      {c.rating != null && c.ratingCount > 0 && (
                        <div className="flex items-center gap-1.5 mb-3">
                          <Star size={14} className="fill-current" style={{ color: '#F59E0B' }} />
                          <span className="text-sm font-semibold" style={{ color: V2.heading }}>{c.rating.toFixed(1)}</span>
                          <span className="text-xs" style={{ color: V2.textMute }}>({c.ratingCount})</span>
                        </div>
                      )}

                      {/* Cargo types */}
                      {c.cargoTypes.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {c.cargoTypes.slice(0, 3).map((ct) => (
                            <span key={ct} className="px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wide"
                              style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: V2.accent, border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)' }}>
                              {t(`mkt_cargo_${ct}`)}
                            </span>
                          ))}
                          {c.cargoTypes.length > 3 && (
                            <span className="text-[10px] font-medium" style={{ color: V2.textMute }}>
                              +{c.cargoTypes.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Service countries */}
                      {c.serviceCountries.length > 0 && (
                        <div className="flex items-center gap-1.5 mb-4 text-xs" style={{ color: V2.textMute }}>
                          <MapPin size={12} />
                          <span className="line-clamp-1">
                            {c.serviceCountries.slice(0, 5).map(cc => FLAGS[cc] || cc).join(' ')}
                            {c.serviceCountries.length > 5 ? ` +${c.serviceCountries.length - 5}` : ''}
                          </span>
                        </div>
                      )}

                      {/* CTA */}
                      <div className="pt-3 text-sm font-semibold transition-colors group-hover:text-[var(--accent)]"
                        style={{ color: V2.accent, borderTop: `1px solid ${V2.line}` }}>
                        {t('mkt_index_view')} &rarr;
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="flex items-center gap-1 text-sm px-4 py-2 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors hover:border-[var(--accent)]/40"
                  style={{ color: V2.text, border: `1px solid ${V2.line}`, background: V2.bg2 }}
                >
                  <ChevronLeft size={16} /> {t('companies_prev')}
                </button>
                <span className="px-4 text-sm" style={{ color: V2.textMute }}>
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="flex items-center gap-1 text-sm px-4 py-2 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors hover:border-[var(--accent)]/40"
                  style={{ color: V2.text, border: `1px solid ${V2.line}`, background: V2.bg2 }}
                >
                  {t('companies_next')} <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </section>

      </div>
      <PublicFooter />
    </>
  );
}
